import React, { useEffect, useRef, useMemo } from "react";
import { Button, Dropdown, Typography, Spin, Empty } from "antd";
import { Session } from "../../../../Entities/Session";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import AssessmentRoundCard from "./AssessmentRoundCard";
import useAssessmentStatistics, { RoundStatistics } from "./useAssessmentStatistics";
import { DownOutlined } from "@ant-design/icons";
import { RoundStatusFilter } from "./AssessmentStatusFilter";

interface UnifiedRoundData {
  roundId: string;
  roundNumber: number;
  displayRoundNumber: number;
  title: string;
  isMustQualify: boolean;
  passPercentage?: number;
  sessions: Session[];
  latestSession: Session | null;
  status: 'not-attempted' | 'pending' | 'qualified' | 'unqualified';
  hasAttempted: boolean;
}

interface JobApplicationinterviewRoundsListProps {
  selectedSession: Session | null;
  setSelectedSession: React.Dispatch<React.SetStateAction<Session | null>>;
  interviewSessions: Session[];
  interviewRounds: InterviewRound[];
  jobPost: JobDescriptionEntity;
  roundFilter: RoundStatusFilter;
  questionFilter: "all" | "qualified" | "unqualified" | "pending";
  setQuestionFilter: React.Dispatch<React.SetStateAction<"all" | "qualified" | "unqualified" | "pending">>;
  onStatisticsLoaded?: (stats: {
    all: number;
    qualified: number;
    unqualified: number;
    pending: number;
  }) => void;
  unifiedRounds?: UnifiedRoundData[];
  selectedRoundId?: string | null;
  onRoundSelect?: (roundId: string, sessionId?: string) => void;
}

const JobApplicationinterviewRoundsList: React.FC<JobApplicationinterviewRoundsListProps> = ({
  selectedSession,
  setSelectedSession,
  interviewSessions,
  interviewRounds,
  jobPost,
  roundFilter,
  questionFilter,
  setQuestionFilter,
  onStatisticsLoaded,
  unifiedRounds,
  selectedRoundId,
  onRoundSelect,
}) => {
  const listContentRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Exclude profile-match from the Assessments tab (handled on Summary tab)
  const jobPostInterviewRounds = useMemo(
    () =>
      jobPost.interviewRounds
        .filter((r) => r.interviewRoundId !== null && r.interviewRoundId !== "profile-match")
        .map((r) => ({
          interviewRoundId: r.interviewRoundId as string,
          interviewRoundNumber: r.interviewRoundNumber,
        }))
        .sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber),
    [jobPost.interviewRounds]
  );

  const { statistics, loading } = useAssessmentStatistics({
    interviewSessions,
    interviewRounds,
    jobPostInterviewRounds,
  });

  // Notify parent of statistics when loaded
  useEffect(() => {
    if (statistics && onStatisticsLoaded) {
      onStatisticsLoaded({
        all: statistics.totalRounds,
        qualified: statistics.qualifiedRounds,
        unqualified: statistics.unqualifiedRounds,
        pending: statistics.pendingRounds,
      });
    }
  }, [statistics, onStatisticsLoaded]);

  // Build all rounds data - one entry per attempt (session) plus placeholders for not-attempted rounds
  const allRoundsData = useMemo(() => {
    if (!unifiedRounds || unifiedRounds.length === 0) {
      // Fallback to statistics if unifiedRounds not provided (one card per session)
      if (!statistics) return [];
      return statistics.rounds.map(round => {
        const displayRoundNumber = round.roundNumber === 0 ? 1 : round.roundNumber;
        const attemptSuffix = round.isRestarted ? ` (Attempt ${round.attemptNumber})` : '';
        return {
          ...round,
          displayRoundNumber,
          roundName: `Round ${displayRoundNumber}: ${round.roundName}${attemptSuffix}`,
        };
      });
    }

    const roundsWithStats: (RoundStatistics & { displayRoundNumber: number })[] = [];

    unifiedRounds.forEach((unifiedRound) => {
      if (unifiedRound.sessions.length > 0) {
        // One entry per attempt (session)
        unifiedRound.sessions.forEach((session, idx) => {
          const statsRound = statistics?.rounds.find(
            (r) => r.interviewRoundId === unifiedRound.roundId && r.sessionId === session._id
          );
          const attemptNumber = idx + 1;
          const isRestarted = unifiedRound.sessions.length > 1;
          const attemptSuffix = isRestarted ? ` (Attempt ${attemptNumber})` : '';

          if (statsRound) {
            roundsWithStats.push({
              ...statsRound,
              displayRoundNumber: unifiedRound.displayRoundNumber,
              roundName: `Round ${unifiedRound.displayRoundNumber}: ${unifiedRound.title}${attemptSuffix}`,
            });
          } else {
            // Session exists but stats not loaded yet - still show card with session data
            roundsWithStats.push({
              sessionId: session._id,
              interviewRoundId: unifiedRound.roundId,
              roundName: `Round ${unifiedRound.displayRoundNumber}: ${unifiedRound.title}${attemptSuffix}`,
              roundNumber: unifiedRound.roundNumber,
              displayRoundNumber: unifiedRound.displayRoundNumber,
              attemptNumber,
              isRestarted,
              status: session.status,
              totalQuestions: session.noOfQuestionsGenerated ?? 0,
              attemptedQuestions: 0,
              qualifiedQuestions: 0,
              unqualifiedQuestions: 0,
              pendingQuestions: 0,
              hasViolations: false,
              violationCount: 0,
              questions: [],
              updatedAt: session.updatedAt,
            });
          }
        });
      } else {
        // No sessions - placeholder for this round
        const statusForStats = unifiedRound.status === 'not-attempted' ? 'pending' : unifiedRound.status;
        roundsWithStats.push({
          sessionId: '',
          interviewRoundId: unifiedRound.roundId,
          roundName: `Round ${unifiedRound.displayRoundNumber}: ${unifiedRound.title}`,
          roundNumber: unifiedRound.roundNumber,
          displayRoundNumber: unifiedRound.displayRoundNumber,
          attemptNumber: 0,
          isRestarted: false,
          status: statusForStats as "pending" | "qualified" | "unqualified",
          totalQuestions: 0,
          attemptedQuestions: 0,
          qualifiedQuestions: 0,
          unqualifiedQuestions: 0,
          pendingQuestions: 0,
          hasViolations: false,
          violationCount: 0,
          questions: [],
          updatedAt: new Date(),
        });
      }
    });

    return roundsWithStats;
  }, [unifiedRounds, statistics]);

  const filteredRounds = useMemo(() => {
    if (roundFilter === "all") return allRoundsData;
    return allRoundsData.filter((round) => round.status === roundFilter);
  }, [allRoundsData, roundFilter]);

  // Auto-select first round when filtered rounds change or on initial load
  useEffect(() => {
    if (!loading) {
      if (filteredRounds.length > 0) {
        // If selectedRoundId is provided, sync round id. Prefer keeping current session when it belongs to this round (so Attempt 1 vs Attempt 2 is preserved).
        if (selectedRoundId && onRoundSelect) {
          const roundsForRoundId = filteredRounds.filter((r) => r.interviewRoundId === selectedRoundId);
          const round =
            roundsForRoundId.find((r) => r.sessionId === selectedSession?._id) ?? roundsForRoundId[0];
          if (round) {
            if (round.sessionId) {
              const session = interviewSessions.find((s) => s._id === round.sessionId);
              if (session) {
                if (selectedSession?._id !== session._id) {
                  setSelectedSession(session);
                }
                onRoundSelect(selectedRoundId, round.sessionId);
                return;
              }
            } else {
              setSelectedSession(null);
              onRoundSelect(selectedRoundId);
              return;
            }
          }
        }

        // Check if selectedSession is still valid
        const isSelectedSessionInFilteredRounds = selectedSession
          ? filteredRounds.some((round) => round.sessionId === selectedSession._id)
          : false;

        // Check if selectedRoundId is still valid
        const isSelectedRoundIdInFilteredRounds = selectedRoundId
          ? filteredRounds.some((round) => round.interviewRoundId === selectedRoundId)
          : false;

        // If no session is selected or selected session/round is not in filtered rounds, select the first round that has attempt data
        if (!selectedSession || !isSelectedSessionInFilteredRounds || !isSelectedRoundIdInFilteredRounds) {
          // Prefer first round that has sessionId (attempted and we have details)
          const firstRoundWithData = filteredRounds.find((r) => r.sessionId);
          const roundToSelect = firstRoundWithData ?? filteredRounds[0];
          if (roundToSelect) {
            if (roundToSelect.sessionId) {
              const session = interviewSessions.find((s) => s._id === roundToSelect.sessionId);
              if (session) {
                setSelectedSession(session);
                onRoundSelect?.(roundToSelect.interviewRoundId, roundToSelect.sessionId);
              } else {
                setSelectedSession(null);
                onRoundSelect?.(roundToSelect.interviewRoundId);
              }
            } else {
              setSelectedSession(null);
              onRoundSelect?.(roundToSelect.interviewRoundId);
            }
          }
        }
      } else if (filteredRounds.length === 0) {
        // Clear selection if no filtered rounds are available
        setSelectedSession(null);
      }
    }
  }, [filteredRounds, loading, interviewSessions, selectedSession, setSelectedSession, selectedRoundId, onRoundSelect]);

  const handleSessionSelection = (sessionId: string, roundId?: string) => {
    // Prevent selection if there are no filtered rounds or still loading
    if (filteredRounds.length === 0 || loading) return;

    // If roundId is provided and round has no session, just select the round
    if (roundId && !sessionId) {
      onRoundSelect?.(roundId);
      setSelectedSession(null);
      return;
    }

    const session = interviewSessions.find((s) => s._id === sessionId);
    if (!session) return;

    // Verify the session is in the filtered rounds
    const isSessionInFilteredRounds = filteredRounds.some((round) => round.sessionId === sessionId);
    if (!isSessionInFilteredRounds) return;

    const isSelected = selectedSession?._id === sessionId;
    if (isSelected) return;

    setSelectedSession(session);
    // Update round id only; parent must not overwrite selectedSession (pass sessionId so parent skips setSelectedSession).
    if (roundId) {
      onRoundSelect?.(roundId, sessionId);
    } else {
      const round = filteredRounds.find(r => r.sessionId === sessionId);
      if (round) {
        onRoundSelect?.(round.interviewRoundId, sessionId);
      }
    }
  };

  useEffect(() => {
    if (selectedSession && listContentRef.current) {
      const selectedItemRef = itemRefs.current.get(selectedSession._id);
      if (selectedItemRef) {
        selectedItemRef.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [selectedSession]);

  // Dropdown items for mobile - only rounds with attempt data (sessionId) are selectable
  const dropdownItems = filteredRounds
    .filter((round) => round.sessionId)
    .map((round) => ({
      key: round.sessionId,
      label: `${round.roundName} (${round.status})`,
    }));

  return (
    <div className="job-application-interview-rounds-list-container">
      <div className="job-application-interview-rounds-list-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Assessments
        </Typography.Title>

        <div className="job-application-interview-rounds-list-header-dropdown">
          <Dropdown
            menu={{
              items: dropdownItems,
              selectable: true,
              multiple: false,
              onClick: (e) => {
                e.domEvent.stopPropagation();
                if (dropdownItems.length > 0) {
                  const round = filteredRounds.find(r => r.sessionId === e.key);
                  if (round) handleSessionSelection(e.key, round.interviewRoundId);
                }
              },
              defaultSelectedKeys: [selectedSession?._id || ""],
            }}
            trigger={["click"]}
            disabled={dropdownItems.length === 0 || loading}
          >
            <Button 
              type="default" 
              size="small"
              disabled={dropdownItems.length === 0 || loading}
            >
              {filteredRounds.find((round) => round.sessionId === selectedSession?._id)?.roundName || "Select Round"}
              &nbsp;
              <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      <div ref={listContentRef} className="job-application-interview-rounds-list-content">
        {loading ? (
          <div className="assessment-list-loading">
            <Spin size="small" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Loading...
            </Typography.Text>
          </div>
        ) : filteredRounds.length === 0 ? (
          <div className="assessment-list-empty">
            <Empty
              description={
                roundFilter === "all"
                  ? "No assessments"
                  : `No ${roundFilter} rounds`
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
          filteredRounds.map((round) => {
            const roundKey = round.sessionId || round.interviewRoundId;
            const isSelected = selectedSession?._id === round.sessionId;
            
            return (
              <div
                key={roundKey}
                ref={(el) => {
                  if (el && round.sessionId) {
                    itemRefs.current.set(round.sessionId, el);
                  } else if (!round.sessionId) {
                    itemRefs.current.delete(roundKey);
                  }
                }}
              >
                <AssessmentRoundCard
                  round={round}
                  isSelected={isSelected}
                  onClick={() => handleSessionSelection(round.sessionId, round.interviewRoundId)}
                  statusFilter={questionFilter}
                  onQuestionFilterChange={setQuestionFilter}
                  disabled={!round.sessionId}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default JobApplicationinterviewRoundsList;
